import React, { useCallback, useMemo, useState } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useSubscription } from 'react-relay';
import { SuccessAutoDismissFlag, type FlagId } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
	fireUIAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type {
	AsyncExportIssuesCommonInput,
	AsyncExportItem,
	ExportIssuesSubscriptionError,
} from '../../../common/types.tsx';
import { useExportIssuesStateActions } from '../../../controllers/export-issues-state/index.tsx';
import type {
	exportIssuesTaskFlagSubscription,
	exportIssuesTaskFlagSubscription$data,
} from './__generated__/exportIssuesTaskFlagSubscription.graphql';
import { ExportIssuesTaskDownloadFlag } from './export-issues-task-download-flag/index.tsx';
import { ExportIssuesTaskErrorFlag } from './export-issues-task-error-flag/index.tsx';
import { ExportIssuesTaskProgressFlag } from './export-issues-task-progress-flag/index.tsx';
import messages from './messages.tsx';
import { getExportIssuesStatus } from './utils.tsx';

type Props = {
	exportItem: AsyncExportItem;
	flagId: FlagId;
	onDismissFlag: (flagId: FlagId) => void;
	commonInput: AsyncExportIssuesCommonInput;
};

export const ExportIssuesTaskFlag = ({
	exportItem,
	onDismissFlag: onDismissFlagProp,
	flagId,
	commonInput,
}: Props): React.JSX.Element => {
	const cloudId = useCloudId();
	const { formatMessage } = useIntl();
	const [exportIssuesTask, setExportIssuesTask] = useState<NonNullable<
		NonNullable<exportIssuesTaskFlagSubscription$data['jira']>['onIssueExported']
	> | null>(null);
	const [error, setError] = useState<ExportIssuesSubscriptionError | undefined>();
	const [downloadCompleted, setDownloadCompleted] = useState(false);
	const { removeExportInProgress } = useExportIssuesStateActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleOnDownloadComplete = useCallback(() => {
		setDownloadCompleted(true);

		fireTrackAnalytics(createAnalyticsEvent({}), 'JiraIssueExportTask downloaded', {
			taskId: exportIssuesTask?.task?.id,
		});
	}, [createAnalyticsEvent, exportIssuesTask?.task?.id]);

	const onDismissFlag = useCallback(() => {
		removeExportInProgress(exportItem.id);
		onDismissFlagProp(flagId);

		/**
		 * We don't want to fire the dismissed event if the download has completed because is autodismissed
		 */
		if (!downloadCompleted) {
			const cancelEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			});
			fireUIAnalytics(cancelEvent, 'dismissedJiraIssueExportTask', {
				exportStatus: getExportIssuesStatus({
					__typename: exportIssuesTask?.__typename,
					downloadCompleted,
					hasError: !!error,
				}),
			});
		}
	}, [
		createAnalyticsEvent,
		downloadCompleted,
		error,
		exportIssuesTask?.__typename,
		exportItem.id,
		flagId,
		onDismissFlagProp,
		removeExportInProgress,
	]);

	const handleOnNextSubscription = useCallback(
		(response: exportIssuesTaskFlagSubscription$data | undefined | null) => {
			setExportIssuesTask(response?.jira?.onIssueExported || null);

			if (response?.jira?.onIssueExported?.__typename === 'JiraIssueExportTaskCompleted') {
				removeExportInProgress(exportItem.id);

				fireOperationalAnalytics(createAnalyticsEvent({}), 'JiraIssueExportTask ExportSuccess', {
					taskId: response.jira.onIssueExported.task?.id,
				});
			}

			if (response?.jira?.onIssueExported?.__typename === 'JiraIssueExportTaskTerminated') {
				removeExportInProgress(exportItem.id);
			}
		},
		[createAnalyticsEvent, exportItem.id, removeExportInProgress],
	);

	const handleOnErrorSubscription = useCallback(
		(exportIssuesSubscriptionError: ExportIssuesSubscriptionError) => {
			removeExportInProgress(exportItem.id);
			setError(exportIssuesSubscriptionError);
		},
		[exportItem.id, removeExportInProgress],
	);

	useSubscription<exportIssuesTaskFlagSubscription>(
		useMemo(
			() => ({
				// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
				subscription: graphql`
					subscription exportIssuesTaskFlagSubscription($input: JiraIssueExportInput!) {
						jira @optIn(to: "JiraAsyncExportIssues") {
							onIssueExported(input: $input) {
								__typename
								...exportIssuesTaskProgressFlag_issueNavigatorActionExportIssues
								...exportIssuesTaskDownloadFlag_issueNavigatorActionExportIssues
								...exportIssuesTaskErrorFlag_issueNavigatorActionExportIssues
								... on JiraIssueExportTaskCompleted {
									task {
										id
									}
								}
							}
						}
					}
				`,
				variables: {
					input: {
						...commonInput,
						cloudId,
						maxResults: exportItem.maxResults,
						exportType: exportItem.exportType,
					},
				},
				onNext: handleOnNextSubscription,
				onError: handleOnErrorSubscription,
			}),
			[
				cloudId,
				commonInput,
				exportItem.exportType,
				exportItem.maxResults,
				handleOnErrorSubscription,
				handleOnNextSubscription,
			],
		),
	);

	if (error || exportIssuesTask?.__typename === 'JiraIssueExportTaskTerminated') {
		return (
			<ExportIssuesTaskErrorFlag
				exportIssuesTask={exportIssuesTask}
				exportItem={exportItem}
				error={error}
				onDismissed={onDismissFlag}
			/>
		);
	}

	if (downloadCompleted) {
		return (
			<SuccessAutoDismissFlag
				id="completed-flag"
				title={formatMessage(messages.exportIssuesFlagExportComplete)}
				onDismissed={onDismissFlag}
				messageId="issue-navigator-action-export-issues.ui.export-item.export-issues-task-flag.success-auto-dismiss-flag"
				messageType="transactional"
			/>
		);
	}

	if (exportIssuesTask?.__typename === 'JiraIssueExportTaskCompleted') {
		return (
			<ExportIssuesTaskDownloadFlag
				exportIssuesTask={exportIssuesTask}
				exportItem={exportItem}
				onDismissed={onDismissFlag}
				onComplete={handleOnDownloadComplete}
			/>
		);
	}

	return (
		<ExportIssuesTaskProgressFlag
			exportIssuesTask={exportIssuesTask}
			exportItem={exportItem}
			onDismissed={onDismissFlag}
		/>
	);
};
